import { Alert, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useLocation } from 'react-router';

import AuthPageShell from '@/components/auth/AuthPageShell';
import ClientInvitationForm from '@/components/form/ClientInvitationForm';
import RecruiterInvitationForm from '@/components/form/RecruiterInvitationForm';
import { LOGIN } from '@/constants/routes';
import { useInvitedUserDetails } from '@/hooks/queries/useInvitedUserDetails';
import { roleFlagsFromRole } from '@/hooks/useCurrentUser';
import { useUrlState } from '@/hooks/useUrlState';
import { PageTitle } from '@/UI/typography';

import { inviteDetailsAlertKey } from './inviteDetailsAlertKey';

export interface UrlParams {
  uid: string;
  token: string;
}

const InvitationConfirm = () => {
  const [hasCompletedInvitation, setHasCompletedInvitation] = useState(false);
  const { uid, token } = useParams() as unknown as UrlParams;
  const { history } = useUrlState();
  const { t } = useTranslation();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const profileId = searchParams.get('profile_id');

  const {
    data: invitedUser,
    error,
    isError,
    isPending,
  } = useInvitedUserDetails(uid, token, profileId, {
    enabled: !hasCompletedInvitation,
  });

  const user = invitedUser && !invitedUser.is_complete ? invitedUser : null;
  const { isClient, isRecruiter } = roleFlagsFromRole(user?.role);
  const isValid =
    hasCompletedInvitation || (!isError && (isPending || Boolean(invitedUser)));

  useEffect(() => {
    if (invitedUser?.is_complete) {
      history.push(LOGIN);
    }
  }, [invitedUser, history]);

  return (
    <AuthPageShell bannerTitle={t('GET_STARTED_LABEL')}>
      {isValid ? (
        <>
          {user && isClient && (
            <ClientInvitationForm
              user={user}
              profileId={profileId ?? undefined}
              onInvitationComplete={() => setHasCompletedInvitation(true)}
            />
          )}
          {user && isRecruiter && (
            <RecruiterInvitationForm
              user={user}
              onInvitationComplete={() => setHasCompletedInvitation(true)}
            />
          )}
        </>
      ) : (
        <>
          <Alert
            type="error"
            showIcon={false}
            title={t(inviteDetailsAlertKey(error))}
          />
          <PageTitle>
            <Typography.Text>
              {t('GO_TO_TITLE')} <Link to={LOGIN}>{t('LOG_IN_TEXT')}</Link>
            </Typography.Text>
          </PageTitle>
        </>
      )}
    </AuthPageShell>
  );
};

export default InvitationConfirm;
